import React, { useContext, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  allKpisStructure,
  allSearchBarFilters as dashboardFilters, allWidgetStructure as dashboardWidgets,
} from 'conf/pages/dashboardConf';
import {
  allSearchBarFilters as myDataFilters,
  allTableColumns,
  allWidgetStructure as mydataWidgets,
} from 'conf/pages/myDataConf';
import {
  ADMIN_USER_TYPE,
  ADMIN_VIEW_USER_TYPE,
  CONFIG_ADMIN_USER_TYPE,
  CONFIG_ADMIN_VIEW_USER_TYPE,
  ENERGY_PRODUCTION_WIDGET,
  KPIS_SIDEMENU_OPTION,
  MEMBER_USER_TYPE,
  MEMBER_VIEW_USER_TYPE,
  MONTH,
  QUICKSTATS_SIDEMENU_OPTION,
  RESOURCE_TYPE_DASHBOARD,
  RESOURCE_TYPE_ENERGY_COMMUNITY,
  routes,
  RP_CONFIGURATION,
  RP_EC_ID,
  RP_POD,
  SEARCHBAR_SIDEMENU_OPTION,
  TABLE_SIDEMENU_OPTION,
  TEMPLATE_PAGE_DASHBOARD,
  TEMPLATE_PAGE_ENERGY_COMMUNITY,
} from 'consts';
import { Context } from 'context/Context';
import NotAuthorizedError from 'errors/notAuthorizedError';
import { EnelXGuiComponents, EnelXGuiTemplates } from 'gui';
import useFormatter from 'hook/useFormatter';
import usePageSettings from 'hook/usePageSettings';
import useRoleBase from 'hook/useRoleBase/useRoleBase';
import useTemplate from 'hook/useTemplate';
import moment from 'moment';
import { useTheme } from 'styled-components';
import {
  DEFAULT_PAGINATION_PAGE_SIZE,
  divideArray,
  entityMap,
  findRole, generalSearchBarFetchFilterFormatter, handleExternalFavTemplate,
  mapOptions,
  WidgetDisclaimer,
} from 'utils';

import CustomSearchbar from 'components/CustomSearchbar';
import Footer from 'components/Footer';

const { MigrationThemedContainer } = EnelXGuiTemplates;

const {
  MigrationMainTable,
  MigrationLoader,
  MigrationTemplateWidget,
  MigrationMainKpiGroup,
} = EnelXGuiComponents;

const PageComponent = ({
  translationLabel,
  template,
  queryFunction,
}) => {
  const { t } = useTranslation(translationLabel);
  const history = useHistory();

  const {
    accessToken,
    defaultQueryProps,
    api,
    energyCommunities,
    tenant,
    localization: {
      countryID,
    },
    setTicketInfo,
    roleBaseData,
    grants,
  } = useContext(Context);

  const { epoch } = useFormatter();
  const theme = useTheme();

  const templateIDFromHeader = history.location.state?.templateId;

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_PAGE_SIZE);
  const [sorter, setSorter] = useState({});
  const [filters, setFilters] = useState({});

  const [sideMenuToShow, setSideMenuToShow] = useState('');
  const [timeToRefetch, setTimeToRefetch] = useState(null);
  const [granularity, setGranularity] = useState(MONTH);
  const [dataChartType, setDataChartType] = useState('all');

  const [userType, setUserType] = useState('');

  const dateFrom = filters?.startRange;
  const dateTo = filters?.endRange;
  const isDateDiffUnder30Days = moment(dateTo).diff(moment(dateFrom), 'days') <= 30;

  let rolebaseObj;
  if (roleBaseData && userType) {
    const rolebase = roleBaseData.find((elem) => elem.role === userType);
    if (!rolebase) {
      throw new NotAuthorizedError('Not authorized', {
        reason: `Role ${userType} not found in rolebase`,
      });
    } else {
      rolebaseObj = rolebase;
    }
  }

  const {
    availableResources,
    roleBaseFunctions,
  } = useRoleBase(rolebaseObj);

  const {
    isPrimaryActionAvailable,
    isWidgetAvailable,
    isThreeDotActionAvailable,
  } = roleBaseFunctions ?? {};

  const {
    data: {
      appPageTemplates: {
        isGetPageTemplatesFetching,
      },
      appActiveTemplates: {
        isGetActiveTemplateFetching,
      },
      templateConditionToFetch,
    },
    state: {
      get: {
        currentTemplate,
        templateProperties,
        templateList,
        modalType,
        isShowFavTempModal,
        customTemplateInput,
      },
      set: {
        setCurrentTemplate,
        setIsShowFavTempModal,
      },
    },
    func: {
      getTemplateById,
      editCurrentTemplate,
      deleteCurrentTemplate,
      saveCurrentTemplate,
      updateTemplateList,
      createTemplateDraft,
      modifyAnotherTemplate,
    },
  } = useTemplate(template);

  const hasEnergyCommunityFilter = !!filters?.energyCommunityFilter;
  const hasConfigurationFilter = !!filters?.configurationFilter;
  const hasPodFilter = !!filters?.podFilter;

  let conditionToFetch = false;

  if (template === TEMPLATE_PAGE_DASHBOARD) {
    if ((userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE) && hasEnergyCommunityFilter) {
      conditionToFetch = true;
    } else if ((userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE)
    && hasEnergyCommunityFilter && hasConfigurationFilter) {
      conditionToFetch = true;
    } else if ((userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE)
    && hasEnergyCommunityFilter && hasConfigurationFilter && hasPodFilter) {
      conditionToFetch = true;
    }
  }

  if (template === TEMPLATE_PAGE_ENERGY_COMMUNITY) {
    if ((userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE)
    && hasEnergyCommunityFilter && hasConfigurationFilter) {
      conditionToFetch = true;
    } else if ((userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE)
    && hasEnergyCommunityFilter && hasConfigurationFilter && hasPodFilter) {
      conditionToFetch = true;
    } else if ((userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE)
    && hasEnergyCommunityFilter && hasConfigurationFilter && hasPodFilter) {
      conditionToFetch = true;
    }
  }

  const { data: processedData, isFetching: isFetchingRevenue } = useQuery(
    [`getRevenue@${template}`, tenant, filters, energyCommunities, granularity, userType, sorter, pagination],
    async () => queryFunction({
      api,
      filters,
      pagination,
      granularity,
      userType,
      setTimeToRefetch,
      sorter,
    }),
    {
      ...defaultQueryProps,
      refetchInterval: moment(timeToRefetch).diff(moment()),
      enabled:
      !!accessToken
      && !!tenant
      && templateConditionToFetch
      && conditionToFetch,
    },
  );

  const ecOptions = mapOptions(energyCommunities);

  const ecFilter = {
    id: RP_EC_ID,
    type: 'select',
    label: t('filters.energyCommunity'),
    options: ecOptions,
    disableChipClose: true,
    defaultValue: filters?.energyCommunityFilter,
    onSelect: (value) => {
      const nextRole = findRole(energyCommunities.find((ec) => ec.ecId === value), grants);
      const selectedEcObj = energyCommunities.find((ec) => ec.ecId === value);
      const firstConfig = selectedEcObj?.configurations[0]?.ecId;
      const firstPod = selectedEcObj?.configurations[0]?.loadAreas[0]?.ecId;
      setUserType(nextRole);
      if (nextRole === ADMIN_USER_TYPE || nextRole === ADMIN_VIEW_USER_TYPE) {
        setFilters((prev) => ({
          ...prev,
          energyCommunityFilter: value,
        }));
        currentTemplate.searchbar.filtersValue = [
          {
            id: RP_EC_ID,
            value,
          },
        ];
        setCurrentTemplate(currentTemplate);
      }
      if (nextRole === CONFIG_ADMIN_USER_TYPE || nextRole === CONFIG_ADMIN_VIEW_USER_TYPE) {
        setFilters((prev) => ({
          ...prev,
          energyCommunityFilter: value,
          configurationFilter: firstConfig,
        }));
        currentTemplate.searchbar.filtersValue = [
          {
            id: RP_EC_ID,
            value,
          },
          {
            id: RP_CONFIGURATION,
            value: firstConfig,
          },
        ];
        setCurrentTemplate(currentTemplate);
      }
      if (nextRole === MEMBER_USER_TYPE || nextRole === MEMBER_VIEW_USER_TYPE) {
        setFilters((prev) => ({
          ...prev,
          energyCommunityFilter: value,
          configurationFilter: firstConfig,
          podFilter: firstPod,
        }));
        currentTemplate.searchbar.filtersValue = [
          {
            id: RP_EC_ID,
            value,
          },
          {
            id: RP_CONFIGURATION,
            value: firstConfig,
          },
          {
            id: RP_POD,
            value: firstPod,
          },
        ];
        setCurrentTemplate(currentTemplate);
      }
    },
  };

  const filterProps = {
    t, epoch, energyCommunities, filters, setFilters, currentTemplate, setCurrentTemplate, userType,
  };

  const widgetProps = {
    t,
    widgetsData: processedData,
    theme,
    isLoading: isFetchingRevenue,
    countryID,
    granularity,
    setGranularity,
    dataChartType,
    setDataChartType,
    userType,
    isDateDiffUnder30Days,
  };

  const pageConfigMapper = {
    [TEMPLATE_PAGE_DASHBOARD]: {
      filters: dashboardFilters(filterProps),
      widgets: dashboardWidgets({
        ...widgetProps,
        isDashboard: true,
      }),
      resource: RESOURCE_TYPE_DASHBOARD,
    },
    [TEMPLATE_PAGE_ENERGY_COMMUNITY]: {
      filters: myDataFilters(filterProps),
      widgets: mydataWidgets({
        ...widgetProps,
        podValue: filters?.podFilter,
        isDashboard: false,
      }),
      resource: RESOURCE_TYPE_ENERGY_COMMUNITY,
    },
  };

  const {
    visibleSearchBarFiltersIds,
    fullConfAvailableSearchBarFilters,
    saveNewTemplateToggles,
    fullConfVisibleWidgets,
    sideMenuData,
    allAvaillableTableColumnsProps,
    visibleTableColumns,
    fullConfAvailableTableColumns,
    fullConfVisibleKpis,
  } = usePageSettings(
    availableResources,
    currentTemplate,
    pageConfigMapper[template].resource,
    [
      ecFilter,
      ...pageConfigMapper[template].filters,
    ],
    allKpisStructure({
      t, isFetchingRevenue, processedData, isWidgetAvailable, countryID, userType,
    }),
    allTableColumns({
      t, countryID, userType, setSorter,
    }),
    pageConfigMapper[template].widgets,
    setCurrentTemplate,
  );

  const tableData = entityMap(processedData?.tableData ?? [], allAvaillableTableColumnsProps);

  const submit = (currentFilters) => {
    const normalizedFilterFromSearchBar = generalSearchBarFetchFilterFormatter(
      currentFilters,
    );
    setPagination({ ...DEFAULT_PAGINATION_PAGE_SIZE });
    setFilters(normalizedFilterFromSearchBar);
    currentTemplate.searchbar.filtersValue = currentFilters;
    setCurrentTemplate(currentTemplate);
  };

  const isPreviewAvailable = isThreeDotActionAvailable
    ? isThreeDotActionAvailable(RESOURCE_TYPE_ENERGY_COMMUNITY, 'preview@energyCommunity') : false;

  const selectedEcObj = Array.isArray(energyCommunities) ? energyCommunities.find(
    (ec) => ec.ecId === filters?.energyCommunityFilter,
  ) : undefined;
  const selectedConfigObj = Array.isArray(selectedEcObj?.configurations)
    ? selectedEcObj.configurations.find((configObj) => configObj.ecId === filters?.configurationFilter) : undefined;
  const selectedPodObj = Array.isArray(selectedConfigObj?.loadAreas)
    ? selectedConfigObj.loadAreas.find((pod) => pod.ecId === filters?.podFilter) : undefined;

  const canEditFilters = isPrimaryActionAvailable ? isPrimaryActionAvailable('editFilters@mydata') : false;
  const canEditWidgets = isPrimaryActionAvailable ? isPrimaryActionAvailable('editWidgets@mydata') : false;
  const canEditTable = isPrimaryActionAvailable ? isPrimaryActionAvailable('editTable@mydata') : false;
  const canLinkToMyData = isPrimaryActionAvailable ? isPrimaryActionAvailable('linkToMydata@homepage') : false;

  useEffect(() => {
    handleExternalFavTemplate({
      getTemplateCb: getTemplateById,
      history,
      templateIDFromHeader,
      templateList,
      updateTemplateCb: updateTemplateList,
    });
  }, [templateIDFromHeader, templateList]);

  useEffect(() => {
    let toSet = {};
    if (userType === MEMBER_USER_TYPE) {
      toSet = {
        ecName: selectedEcObj?.name,
        configName: selectedConfigObj?.name,
        podName: selectedPodObj?.participantName,
        accountId: selectedPodObj?.accountId,
        segment: selectedPodObj?.businessSegment,
      };
    }
    if (userType === ADMIN_USER_TYPE || userType === CONFIG_ADMIN_USER_TYPE) {
      toSet = {
        ecName: selectedEcObj?.name,
        configName: selectedConfigObj?.name,
        accountId: selectedEcObj?.accountId,
        segment: selectedConfigObj?.businessSegment,
      };
    }
    setTicketInfo(toSet);
    setGranularity(MONTH);
  }, [filters]);

  useEffect(() => {
    // TROVA RUOLO PRIMA EC
    if (!filters?.energyCommunityFilter) {
      const ec = energyCommunities[0];
      const role = findRole(ec, grants);
      setUserType(role);
      setFilters((prev) => ({
        ...prev,
        energyCommunityFilter: ec.ecId,
        configurationFilter: ec.configurations[0].ecId,
        podFilter: ec.configurations[0].loadAreas[0].ecId,
      }));
    }
  }, [filters?.energyCommunityFilter]);

  const performanceWidget = fullConfVisibleWidgets?.find((obj) => obj.id === 'energyPerformanceWidget');
  const energyWidget = fullConfVisibleWidgets?.find((obj) => obj.id === ENERGY_PRODUCTION_WIDGET);

  const dashboardRender = (
    <>
      <div className="mb-3">
        <MigrationMainKpiGroup
          id="ADMIN"
          title={t('widgets.kpi')}
          kpisArray={fullConfVisibleKpis ? divideArray(fullConfVisibleKpis) : []}
          settings={canEditWidgets ? () => setSideMenuToShow(KPIS_SIDEMENU_OPTION) : null}
          isHeaderCustom
          customRightButton={canLinkToMyData ? {
            iconName: 'arrow-right',
            iconSide: 'right',
            iconSize: '12px',
            iconLibrary: 'system',
            callback: () => history.push(routes.energyCommunity.root),
            type: 'secondary',
            text: isMobileOnly ? null : t('seeAllMyData'),
          } : null}
        />
      </div>

      {userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE ? (
        <>
          {performanceWidget ? (
            <div className="mb-3">
              <MigrationTemplateWidget
                fullWidth
                isMinimizable
                translation={t('components')}
                title={t('widgets.insights')}
                settings={canEditWidgets ? () => setSideMenuToShow(QUICKSTATS_SIDEMENU_OPTION) : null}
                widgetList={[performanceWidget]}
              />
            </div>
          ) : null}
          {energyWidget ? (
            <MigrationTemplateWidget
              fullWidth
              isMinimizable
              translation={t('components')}
              title={t('widgets.quickStats')}
              widgetList={[energyWidget]}
              settings={canEditWidgets ? () => setSideMenuToShow(QUICKSTATS_SIDEMENU_OPTION) : null}
              footer={<WidgetDisclaimer text={t('widgetDisclaimer')} />}
            />
          ) : null}
        </>
      ) : (
        <MigrationTemplateWidget
          fullWidth
          isMinimizable
          translation={t('components')}
          title={t('widgets.quickStats')}
          widgetList={fullConfVisibleWidgets}
          settings={canEditWidgets ? () => setSideMenuToShow(QUICKSTATS_SIDEMENU_OPTION) : null}
          footer={<WidgetDisclaimer text={t('widgetDisclaimer')} />}
        />
      )}
    </>
  );

  const myDataRender = (
    <>
      <div className="mb-3">
        <MigrationTemplateWidget
          fullWidth
          isMinimizable
          translation={t('components')}
          title={t('widgets.quickStats')}
          widgetList={fullConfVisibleWidgets}
          settings={canEditWidgets ? () => setSideMenuToShow(QUICKSTATS_SIDEMENU_OPTION) : null}
          footer={<WidgetDisclaimer text={t('widgetDisclaimer')} />}
        />
      </div>

      <MigrationMainTable
        translation={t('components')}
        id="operatorsListTable"
        setting={canEditTable ? () => setSideMenuToShow(TABLE_SIDEMENU_OPTION) : null}
        tableData={tableData ?? []}
        columns={visibleTableColumns ? visibleTableColumns() : []}
        size={processedData?.totalResults ?? 0}
        pageNum={pagination?.pageNum}
        pageSize={pagination?.pageSize}
        pagination={(currentPageNum, currentPageSize) => setPagination(
          { pageNum: currentPageNum, pageSize: currentPageSize },
        )}
        automaticPreview={isPreviewAvailable ? fullConfAvailableTableColumns : null}
      />
    </>
  );

  const renderMapper = {
    [TEMPLATE_PAGE_DASHBOARD]: dashboardRender,
    [TEMPLATE_PAGE_ENERGY_COMMUNITY]: myDataRender,
  };

  return (
    <>
      {(
        isGetActiveTemplateFetching
        || isGetPageTemplatesFetching
        || isFetchingRevenue
      ) ? <MigrationLoader /> : null}

      <MigrationThemedContainer
        translation={t('components')}
        buttonWidth="175px"
        history={history}
        pageTitle={<h1 className="text-3xl leading-10">{`${t('filters.energyCommunity')}: ${selectedEcObj?.name ?? '-'}`}</h1>}
        templateList={templateList}
        modalType={modalType}
        isShowSaveTemplate={isShowFavTempModal}
        closeSaveTemplate={() => setIsShowFavTempModal(false)}
        closeMenu={() => setSideMenuToShow('')}
        editTemplateFunc={editCurrentTemplate}
        deleteTemplateFunc={deleteCurrentTemplate}
        newTemporaryFunc={createTemplateDraft}
        saveTemplateFunc={saveCurrentTemplate}
        templateInput={templateProperties}
        sideMenuBtnName={t('components.save')}
        isShowMenu={!!sideMenuToShow}
        menuData={sideMenuData}
        sideMenuToShow={sideMenuToShow}
        menuBtnFunc={(updatedToggles) => {
          saveNewTemplateToggles(updatedToggles);
          setSideMenuToShow('');
        }}
        openEditTemplate={modifyAnotherTemplate}
        customTemplateInput={customTemplateInput}
      >
        <div className="mb-3">
          <CustomSearchbar
            submit={submit}
            values={fullConfAvailableSearchBarFilters}
            visibleIds={visibleSearchBarFiltersIds}
            filters={currentTemplate.searchbar.filtersValue}
            settingCb={canEditFilters ? () => setSideMenuToShow(SEARCHBAR_SIDEMENU_OPTION) : null}
            templateCb={() => setIsShowFavTempModal(true)}
          />
        </div>

        {renderMapper[template]}
      </MigrationThemedContainer>
      <Footer isPrimaryActionAvailable={isPrimaryActionAvailable} />
    </>
  );
};

export default PageComponent;
